import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, ImageSquare } from "@phosphor-icons/react";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { INDUSTRIES } from "@/lib/industries";

const EMPTY = {
  name: "", category: "", address: "", city: "", state: "", zip: "",
  country: "United States", phone: "", website: "", email: "", hours: "", description: "",
};

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">{label}</Label>
    {children}
  </div>
);

export default function BusinessFormDialog({ open, onOpenChange, business, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [customCategory, setCustomCategory] = useState(false);
  const [logoPath, setLogoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const editing = Boolean(business);

  useEffect(() => {
    if (open) {
      const next = business ? { ...EMPTY, ...business } : EMPTY;
      setForm(next);
      setLogoPath(business?.logo_path || "");
      setCustomCategory(Boolean(next.category) && !INDUSTRIES.includes(next.category));
    }
  }, [open, business]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!business?.business_id) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Please upload a PNG, JPG, WEBP or GIF image");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be under 5 MB");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/businesses/${business.business_id}/logo`, fd);
      setLogoPath(data.logo_path);
      toast.success("Logo uploaded");
      onSaved && onSaved({ ...business, logo_path: data.logo_path });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!business?.business_id) return;
    try {
      await api.delete(`/businesses/${business.business_id}/logo`);
      setLogoPath("");
      toast.success("Logo removed");
      onSaved && onSaved({ ...business, logo_path: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onCategoryChange = (val) => {
    if (val === "__other__") {
      setCustomCategory(true);
      setForm((f) => ({ ...f, category: "" }));
    } else {
      setCustomCategory(false);
      setForm((f) => ({ ...f, category: val }));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Business name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...EMPTY, ...form };
      delete payload.stats; delete payload.business_id; delete payload.owner_user_id;
      delete payload.created_at; delete payload.updated_at;
      const { data } = editing
        ? await api.put(`/businesses/${business.business_id}`, payload)
        : await api.post("/businesses", payload);
      toast.success(editing ? "Business updated" : "Business added");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="business-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">
            {editing ? "Edit business" : "Add a business"}
          </DialogTitle>
          <DialogDescription>Your NAP details are reused to generate listings for every directory.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="name" label="Business name *">
              <Input id="name" data-testid="business-name-input" value={form.name} onChange={set("name")} placeholder="Acme Coffee Co." />
            </Field>
            <Field id="category" label="Industry">
              <Select value={customCategory ? "__other__" : (form.category || undefined)} onValueChange={onCategoryChange}>
                <SelectTrigger data-testid="business-category-select"><SelectValue placeholder="Select an industry" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  <SelectItem value="__other__">Other (type your own)</SelectItem>
                </SelectContent>
              </Select>
              {customCategory && (
                <Input data-testid="business-category-input" value={form.category} onChange={set("category")}
                  placeholder="Enter your industry" className="mt-2" />
              )}
            </Field>
          </div>

          <Field id="address" label="Street address">
            <Input id="address" data-testid="business-address-input" value={form.address} onChange={set("address")} placeholder="123 Main St" />
          </Field>

          <div className="grid sm:grid-cols-3 gap-5">
            <Field id="city" label="City"><Input id="city" data-testid="business-city-input" value={form.city} onChange={set("city")} /></Field>
            <Field id="state" label="State"><Input id="state" data-testid="business-state-input" value={form.state} onChange={set("state")} placeholder="CA" /></Field>
            <Field id="zip" label="ZIP"><Input id="zip" data-testid="business-zip-input" value={form.zip} onChange={set("zip")} /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="phone" label="Phone"><Input id="phone" data-testid="business-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" /></Field>
            <Field id="website" label="Website"><Input id="website" data-testid="business-website-input" value={form.website} onChange={set("website")} placeholder="https://" /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="email" label="Email"><Input id="email" data-testid="business-email-input" value={form.email} onChange={set("email")} /></Field>
            <Field id="hours" label="Hours"><Input id="hours" data-testid="business-hours-input" value={form.hours} onChange={set("hours")} placeholder="Mon–Fri 9am–6pm" /></Field>
          </div>

          <Field id="description" label="Business description">
            <Textarea id="description" data-testid="business-description-input" value={form.description} onChange={set("description")} rows={4}
              placeholder="Describe your business, services and what makes you unique…" />
          </Field>

          <Field id="logo" label="Company logo">
            {editing ? (
              <div className="flex items-center gap-4" data-testid="logo-uploader">
                <div className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {logoPath
                    ? <img src={fileUrl(logoPath)} alt="Logo" className="h-full w-full object-contain" data-testid="logo-preview" />
                    : <ImageSquare size={26} className="text-slate-300" />}
                </div>
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
                  <Button type="button" variant="secondary" disabled={uploading} onClick={() => fileRef.current?.click()}
                    data-testid="logo-upload-button" className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                    <UploadSimple size={16} className="mr-1.5" /> {uploading ? "Uploading…" : logoPath ? "Replace" : "Upload logo"}
                  </Button>
                  {logoPath && (
                    <Button type="button" variant="ghost" onClick={removeLogo} data-testid="logo-remove-button"
                      className="text-slate-500 hover:text-red-600">
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Save the business first, then add a logo from Edit info.</p>
            )}
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="business-cancel-button"
            className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="business-save-button"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white">{busy ? "Saving…" : editing ? "Save changes" : "Add business"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
